import React, { useEffect, useState } from 'react';
import { UpdateProcesso } from '@services/Processo/Index.jsx';
import SweetAlert from '@components/Feedback/SweetAlert/Index.jsx';
import ButtonComp from '@components/UI/Button/Index.jsx';
import ValidationsProcesso from '@validations/Processo/Validations.jsx';
import InputTextModalComp from '@components/UI/InputTextModal/Index.jsx';
import SelectOptionModalComp from '@components/UI/SelectOptionModal/Index.jsx';
import { flgStatusOptions } from '@enums/FlgStatus/Index';
import { useFetchDataCascade } from '@hooks/api/useFetchDataCascade.js';
import { Modal } from 'antd';

function EditModal({ onUpdate, visible, closeModal, selectedRow }) {
  const [codigo, setCodigo] = useState('');

  const {
    yearOptions,
    serviceOptions,
    typeOptions,
    yearLoading,
    serviceLoading,
    typeLoading,
    fetchYears,
    fetchServicesByYear,
    fetchTypesByServiceId,
    setServiceOptions,
    setTypeOptions,
  } = useFetchDataCascade();

  const [tempValues, setTempValues] = useState({
    ano: null,
    servico: null,
    tipo: null,
    nomeProcesso: '',
    descricao: '',
    flgStatus: null,
  });

  useEffect(() => {
    fetchYears();
  }, []);

  useEffect(() => {
    if (selectedRow) {
      setCodigo(selectedRow.codigo);

      setTempValues({
        ano: { value: selectedRow.ano, label: selectedRow.ano },
        servico: { value: selectedRow.idTipoServico, label: selectedRow.tipoServico },
        tipo: { value: selectedRow.idTipoServicoAssociado, label: selectedRow.tipoServicoAssociado },
        nomeProcesso: selectedRow.nomeProcesso,
        descricao: selectedRow.descricao,
        flgStatus: selectedRow.flgStatus,
      });

      if (selectedRow.ano) {
        fetchServicesByYear(selectedRow.ano, selectedRow.idTipoServico);
      }
      if (selectedRow.idTipoServico) {
        fetchTypesByServiceId(selectedRow.idTipoServico, selectedRow.idTipoServicoAssociado);
      }
    }
  }, [selectedRow]);

  const checkIsDirty = () => {
    return (
      selectedRow.ano !== tempValues.ano?.value ||
      selectedRow.idTipoServico !== tempValues.servico?.value ||
      selectedRow.idTipoServicoAssociado !== tempValues.tipo?.value ||
      selectedRow.nomeProcesso !== tempValues.nomeProcesso ||
      selectedRow.descricao !== tempValues.descricao ||
      selectedRow.flgStatus !== tempValues.flgStatus
    );
  };

  const handleTempChange = (field, value) => {
    setTempValues(prev => ({
      ...prev,
      [field]: value,
    }));

    if (field === "ano") {
      setServiceOptions([]);
      setTypeOptions([]);
      setTempValues(prev => ({
        ...prev,
        servico: null,
        tipo: null,
      }));
      if (value) {
        fetchServicesByYear(value.value);
      }
    }

    if (field === "servico") {
      setTypeOptions([]);
      setTempValues(prev => ({
        ...prev,
        tipo: null,
      }));
      if (value) {
        fetchTypesByServiceId(value.value);
      }
    }
  };

  const onSubmit = async () => {
    try {
      if (!checkIsDirty()) {
        SweetAlert.error("Erro", "Nenhuma alteração foi feita nos dados.");
        return;
      }

      const { validateFields } = ValidationsProcesso();
      const errors = validateFields(tempValues);

      if (Object.keys(errors).length > 0) {
        const errorMessage = Object.values(errors).join("\n");
        SweetAlert.error("Erro na Validação", errorMessage);
        return;
      }

      const payload = {
        idTipoServicoAssociado: tempValues.tipo?.value,
        nomeProcesso: tempValues.nomeProcesso,
        descricao: tempValues.descricao,
        flgStatus: tempValues.flgStatus,
      };

      SweetAlert.loading();

      const result = await UpdateProcesso(selectedRow.id, payload);

      onUpdate({ ...result });

      closeModal();

      SweetAlert.success('Atualização Concluída!', 'Os dados foram atualizados com sucesso.');
    } catch (error) {

      const errorMessage = error.parsed?.message || 'Não foi possível atualizar o processo.';

      SweetAlert.errorHTML('Erro ao atualizar', errorMessage);
      console.error("Erro ao salvar atualizações", errorMessage);
    }
  };

  const handleCloseWithReset = () => {
    setTempValues({
      ano: { value: selectedRow.ano, label: selectedRow.ano },
      servico: { value: selectedRow.idTipoServico, label: selectedRow.tipoServico },
      tipo: { value: selectedRow.idTipoServicoAssociado, label: selectedRow.tipoServicoAssociado },
      nomeProcesso: selectedRow.nomeProcesso,
      descricao: selectedRow.descricao,
      flgStatus: selectedRow.flgStatus,
    });
    closeModal();
  };

  return (
    <Modal
      getContainer={false}
      open={visible}
      onCancel={handleCloseWithReset}
      centered
      footer={null}
      width={800}
      title={<h5 className="fw-bold">Edição de Processo</h5>}
    >
      <hr />

      <InputTextModalComp
        label="Código"
        value={codigo}
        placeholder="Código"
        disabled
      />

      <SelectOptionModalComp
        label="Ano"
        options={yearOptions}
        value={tempValues.ano}
        onChange={(selected) => handleTempChange('ano', selected)}
        placeholder="Selecionar um ano"
        hoverMessage="Selecionar um ano"
        isLoading={yearLoading}
        isDisabled
      />

      <SelectOptionModalComp
        label="Serviço"
        options={serviceOptions}
        value={tempValues.servico}
        onChange={(selected) => handleTempChange('servico', selected)}
        placeholder="Selecionar um serviço"
        hoverMessage="Selecionar um serviço"
        isLoading={serviceLoading}
        isDisabled
      />

      <SelectOptionModalComp
        label="Tipo"
        options={typeOptions}
        value={tempValues.tipo}
        onChange={(selected) => handleTempChange('tipo', selected)}
        placeholder="Selecionar um tipo"
        hoverMessage="Selecionar um tipo"
        isLoading={typeLoading}
        isDisabled
      />

      <InputTextModalComp
        label="Processo"
        value={tempValues.nomeProcesso}
        onChange={(e) => handleTempChange('nomeProcesso', e.target.value)}
        placeholder="Preencher com um processo"
        hoverMessage="Preencher com um processo"
        maxLength={255}
      />

      <InputTextModalComp
        label="Descrição"
        value={tempValues.descricao}
        onChange={(e) => handleTempChange('descricao', e.target.value)}
        placeholder="Preencher com uma descrição"
        hoverMessage="Preencher com uma descrição"
        maxLength={255}
      />

      <SelectOptionModalComp
        label="Status"
        options={flgStatusOptions}
        value={flgStatusOptions.find(option => option.value === tempValues.flgStatus)}
        onChange={(option) => handleTempChange("flgStatus", option.value)}
        placeholder="Selecionar o status"
        hoverMessage="Selecionar o status"
      />

      <div className="d-flex justify-content-end gap-2 mt-4">
        <ButtonComp
          title="ATUALIZAR"
          buttonColor="#2b97be"
          classnameIcon="fa-regular fa-floppy-disk"
          onClick={onSubmit}
          hoverMessage={"Botão para confirmar a atualização"}
        />

        <ButtonComp
          title="CANCELAR"
          buttonColor="#dc3545"
          classnameIcon="fa-regular fa-circle-xmark"
          onClick={handleCloseWithReset}
          hoverMessage={"Botão para cancelar a atualização"}
        />
      </div>

    </Modal>
  );
}

export default EditModal;